import { useState, useRef, useEffect } from "react";
import { Calendar, ChevronDown } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const DAYS = [
  { value: "MON", label: "Monday" },
  { value: "TUE", label: "Tuesday" },
  { value: "WED", label: "Wednesday" },
  { value: "THU", label: "Thursday" },
  { value: "FRI", label: "Friday" },
  { value: "SAT", label: "Saturday" },
  { value: "SUN", label: "Sunday" },
];

/**
 * Multi-select dropdown for working days.
 */
export const WeekdayPicker = ({ label = "Working Days", value = [], onChange, testId = "weekday-picker" }) => {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const arr = Array.isArray(value) ? value : [];

  useEffect(() => {
    const onClick = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = (v) => {
    if (arr.includes(v)) onChange(arr.filter((x) => x !== v));
    else onChange([...arr, v]);
  };

  const display =
    arr.length === 0
      ? "None"
      : arr.length === 7
      ? "All days"
      : arr.length <= 3
      ? arr.join(", ")
      : `${arr.length} days selected`;

  return (
    <div className="flex items-center justify-between py-1.5 relative" ref={wrap} data-testid={`${testId}-row`}>
      <p className="text-xs font-medium text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
        <Calendar className="w-3 h-3" /> {label}
      </p>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1.5 h-8 px-3 text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md hover:border-slate-400 min-w-[180px] justify-between"
          data-testid={testId}
        >
          <span className="truncate">{display}</span>
          <ChevronDown className="w-3 h-3 text-slate-400 shrink-0" />
        </button>
        {open && (
          <div className="absolute right-0 top-full mt-1 z-[60] w-56 max-h-[320px] overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-xl p-2">
            <button
              type="button"
              className="w-full text-left text-[10px] text-[#8B1A1A] hover:underline mb-1 px-2"
              onClick={() => onChange(arr.length === 7 ? [] : DAYS.map((d) => d.value))}
            >
              {arr.length === 7 ? "Clear all" : "Select all"}
            </button>
            {DAYS.map((d) => (
              <label
                key={d.value}
                className="flex items-center gap-2 py-1 px-2 rounded hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer"
                data-testid={`${testId}-${d.value}`}
              >
                <Checkbox
                  checked={arr.includes(d.value)}
                  onCheckedChange={() => toggle(d.value)}
                />
                <span className="text-xs text-slate-700 dark:text-slate-300">{d.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WeekdayPicker;
